import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { ThemeContextType, ThemeConfig } from '@/types'

// Create Context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Theme Provider Props
interface ThemeProviderProps {
  children: ReactNode
}

// Default theme configuration
const defaultThemeConfig: ThemeConfig = {
  mode: 'auto',
  primaryColor: '#f59e0b', // amber-500
  accentColor: '#3b82f6'   // blue-500
}

// Predefined color themes
export const colorThemes = {
  amber: { primary: '#f59e0b', accent: '#3b82f6', name: '琥珀金' },
  blue: { primary: '#3b82f6', accent: '#f59e0b', name: '海洋藍' },
  emerald: { primary: '#10b981', accent: '#8b5cf6', name: '翡翠綠' },
  purple: { primary: '#8b5cf6', accent: '#f59e0b', name: '紫羅蘭' },
  rose: { primary: '#f43f5e', accent: '#06b6d4', name: '玫瑰紅' },
  teal: { primary: '#14b8a6', accent: '#f97316', name: '青綠色' }
}

// Theme Provider Component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Load theme config from localStorage initially
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(() => {
    try {
      const saved = localStorage.getItem('themeConfig')
      if (saved) {
        return { ...defaultThemeConfig, ...JSON.parse(saved) }
      }
    } catch (error) {
      console.warn('Failed to load theme config from localStorage:', error)
    }
    return defaultThemeConfig
  })

  // Calculate isDark based on theme mode
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (themeConfig.mode === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return themeConfig.mode === 'dark'
  })

  // Listen to system theme changes when mode is 'auto'
  useEffect(() => {
    if (themeConfig.mode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        setIsDark(e.matches)
      }
      
      mediaQuery.addEventListener('change', handleChange)
      setIsDark(mediaQuery.matches)
      
      return () => mediaQuery.removeEventListener('change', handleChange)
    } else {
      setIsDark(themeConfig.mode === 'dark')
    }
  }, [themeConfig.mode])

  // Update document class and CSS variables when theme changes
  useEffect(() => {
    // Update dark mode class
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    // Update CSS custom properties for colors
    const root = document.documentElement
    root.style.setProperty('--color-primary', themeConfig.primaryColor)
    root.style.setProperty('--color-accent', themeConfig.accentColor)

    // Save to localStorage
    localStorage.setItem('themeConfig', JSON.stringify(themeConfig))
  }, [isDark, themeConfig])

  // Toggle theme function (cycles through light -> dark -> auto)
  const toggleTheme = useCallback((): void => {
    setThemeConfig(prev => {
      const modes: ThemeConfig['mode'][] = ['light', 'dark', 'auto']
      const currentIndex = modes.indexOf(prev.mode)
      const nextIndex = (currentIndex + 1) % modes.length
      return { ...prev, mode: modes[nextIndex] }
    })
  }, [])

  // Update theme config function
  const updateThemeConfig = useCallback((config: Partial<ThemeConfig>): void => {
    setThemeConfig(prev => ({ ...prev, ...config }))
  }, [])

  // Reset theme to default
  const resetTheme = useCallback((): void => {
    setThemeConfig(defaultThemeConfig)
  }, [])

  // Context value — useMemo 避免函式參照不變時 re-render 消費端
  const value = useMemo<ThemeContextType>(() => ({
    isDark,
    themeConfig,
    toggleTheme,
    updateThemeConfig,
    resetTheme,
  }), [isDark, themeConfig, toggleTheme, updateThemeConfig, resetTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export default ThemeContext
